import React from 'react';
import { Document, Page, View, Text, Image } from '@react-pdf/renderer';
import { generatedStamp } from '../../../utils/dateTime';
import dawnLogo from '../../../assets/WM-Logo-CB-Transparent-White5-pdf.png';
import buildStyles from './ReportPdfStyles';

const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

// Split a multi-word column header into at most 2 display lines.
const splitHeaderLabel = (label) => {
  const words = label.split(' ');
  if (words.length <= 1) return [label, ''];
  if (words.length === 2) return [words[0], words[1]];
  let bestSplit = 1, bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const m = Math.max(words.slice(0, i).join(' ').length, words.slice(i).join(' ').length);
    if (m < bestMax) { bestMax = m; bestSplit = i; }
  }
  return [words.slice(0, bestSplit).join(' '), words.slice(bestSplit).join(' ')];
};

/**
 * The pages a paginated document (modes 1 and 2 below) is made of, in order:
 * [{ chunk, offset, subtitle?, groupVal?, groupRowCount? }]. Exported so a caller can know the page
 * count before rendering — the server-side renderer uses it to render a large report in slices.
 */
export const buildReportPages = ({ config, data, groupByField, rowsPerPage }) => {
  if (!rowsPerPage) return null;
  if (!groupByField) {
    const pages = [];
    for (let i = 0; i < Math.max(data.length, 1); i += rowsPerPage) {
      pages.push({ chunk: data.slice(i, i + rowsPerPage), offset: i });
    }
    return pages;
  }

  const camelField = toCamelCase(groupByField);
  const displayKey = `${camelField}_Display`;
  const groupColLabel = config.parameters?.find(p => p.field === groupByField)?.label
    || config.columns?.find(c => c.field === groupByField)?.label
    || groupByField;

  // Build ordered group map (insertion order = data order)
  const groupMap = new Map();
  data.forEach(row => {
    const val = row[displayKey] != null ? row[displayKey] : row[camelField];
    const key = val != null ? String(val) : '(Blank)';
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key).push(row);
  });

  // Flatten groups into page descriptors — sorted ascending by group value
  const pages = [];
  [...groupMap.entries()]
    .sort(([a], [b]) => {
      if (a === '(Blank)') return 1;
      if (b === '(Blank)') return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
    })
    .forEach(([groupVal, groupRows]) => {
      const subtitle = `(${groupColLabel}: ${groupVal})`;
      for (let i = 0; i < Math.max(groupRows.length, 1); i += rowsPerPage) {
        pages.push({ subtitle, groupVal, chunk: groupRows.slice(i, i + rowsPerPage), offset: i, groupRowCount: groupRows.length });
      }
    });
  return pages;
};

/**
 * Full PDF document — single page definition with auto-pagination.
 *
 * Modes:
 * 1. groupByField + rowsPerPage — grouped mode: data split by unique field value,
 *    each group paginated at rowsPerPage, header subtitle changes per group.
 * 2. rowsPerPage only — manual pagination without grouping (IO Assignments default).
 * 3. Neither — single <Page>, React-PDF auto-paginates.
 *
 * Slices (modes 1 and 2): pageRange = { start, end, total } renders only pages [start, end) of
 * buildReportPages, numbered "Page start+n of total", and generatedAt fixes the footer timestamp.
 * Layout cost grows faster than page count, so the server renders a large report as slices and
 * merges them; without these props the document renders whole, exactly as before.
 */
const ReportPdfDocument = ({
  config,
  columns,
  columnWidths,
  data,
  parameters,
  paramHeaderWidths = {},
  logoSrc,
  clientLogoSrc,
  clientName,
  sortOrder,
  groupByField,
  rowsPerPage,
  fontSizeOverride,
  qrDataUrl,
  dawnVersion,
  columnsByGroup,
  pageRange,
  generatedAt
}) => {
  const fontSize = fontSizeOverride || config.layout?.fontSize || 8;
  const cellHeight = fontSize * 1.2 + 5.5;
  const styles = buildStyles({ ...config.layout, fontSize });
  const zebraStriping = config.layout?.zebraStriping !== false;
  const timestamp = generatedAt || generatedStamp();
  const pageSize = config.layout?.defaultPageSize?.toUpperCase() === 'LETTER' ? 'LETTER' : 'TABLOID';

  const WatermarkOverlay = () => config.watermark ? (
    <View fixed style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', overflow: 'hidden', opacity: 0.13 }}>
      <View style={{ width: 525, transform: 'rotate(10deg)' }}>
        <Text style={{ fontSize: 49, fontFamily: 'Helvetica-Bold', color: '#000000', textTransform: 'uppercase', letterSpacing: 6, textAlign: 'center' }}>
          {config.watermark}
        </Text>
      </View>
    </View>
  ) : null;

  const PageHeader = ({ fixed, subtitle, totalCount }) => (
    <View style={styles.headerContainer} fixed={fixed || undefined}>
      <View style={styles.headerLeft}>
        {logoSrc && <Image src={logoSrc} style={styles.headerLogo} />}
        {qrDataUrl && (
          <View style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginLeft: 10 }}>
            <Image src={qrDataUrl} style={{ width: 64, height: 64 }} />
            <Text style={{ fontSize: 5, color: '#6b7280', marginTop: 2, textAlign: 'center' }}>Scan to open in DAWN</Text>
          </View>
        )}
      </View>

      <View style={styles.headerTitleOverlay}>
        {config.customTitle && (
          <Text style={{ fontSize: 9, color: config?.branding?.titleColor || '#1f497d', marginBottom: 2, textAlign: 'center', letterSpacing: 0.5 }}>
            {config.customTitle}
          </Text>
        )}
        <Text style={styles.headerTitle}>{config.reportName}</Text>
        {subtitle && (
          <Text style={[styles.headerTitle, { fontSize: 12 }]}>{subtitle}</Text>
        )}
        {totalCount != null && (
          <Text style={{ fontSize: 8, color: '#6b7280', marginTop: 2 }}>{totalCount.toLocaleString()} Records</Text>
        )}
      </View>

      <View style={{ flex: 1 }} />

      {(() => {
        const params = config.parameters || [];
        const getW = (p) => {
          if (p?.field && paramHeaderWidths[p.field]) return paramHeaderWidths[p.field]; // stored as pt
          const px = config.columns?.find(c => c.field === p?.field)?.pixelWidth || 120;
          return Math.max(Math.round(px * 0.5), 60);
        };

        // Fixed 3-col × 2-row grid. slots[0]=pos6 .. slots[5]=pos1.
        // Params fill the last n slots so position 1 (bottom-right) is always first used.
        const NUM_SLOTS = 6;
        const slots = Array(NUM_SLOTS).fill(null);
        params.forEach((p, i) => { slots[NUM_SLOTS - params.length + i] = p; });

        // Column c: top=slots[c*2], bottom=slots[c*2+1]
        const colWidths = [0, 1, 2].map(c =>
          Math.max(slots[c * 2] ? getW(slots[c * 2]) : 0, slots[c * 2 + 1] ? getW(slots[c * 2 + 1]) : 0, 40)
        );
        const totalWidth = colWidths.reduce((sum, w) => sum + w, 0);

        const renderSlot = (slotIdx, colW) => {
          const param = slots[slotIdx];
          if (!param) return <View key={`empty-${slotIdx}`} style={{ width: colW, height: 34 }} />;
          const sels = parameters[param.field] || [];
          return (
            <View key={param.field} style={[styles.paramColumn, { width: colW }]}>
              <Text style={styles.paramLabel}>{param.label}</Text>
              {sels.length === 0 ? (
                <Text style={styles.paramNone}>None Selected</Text>
              ) : (
                sels.slice(0, 4).map((sel, j) => (
                  <Text key={j} style={styles.paramValue}>{sel.fullDisplay || sel.display}</Text>
                ))
              )}
            </View>
          );
        };

        return (
          <View style={[styles.headerRight, { width: totalWidth }]}>
            {/* Row 0 (top): positions 6, 4, 2 = slots 0, 2, 4 */}
            <View style={styles.paramRow}>
              {[0, 2, 4].map((si, ci) => renderSlot(si, colWidths[ci]))}
            </View>
            {/* Row 1 (bottom): positions 5, 3, 1 = slots 1, 3, 5 */}
            <View style={styles.paramRow}>
              {[1, 3, 5].map((si, ci) => renderSlot(si, colWidths[ci]))}
            </View>
          </View>
        );
      })()}
    </View>
  );

  const sortLookup = (sortOrder || []).reduce((acc, s, i) => {
    acc[s.field] = { priority: i + 1, direction: s.direction };
    return acc;
  }, {});

  const ColHeaders = ({ fixed, cols: colsOverride }) => {
    const effectiveCols = colsOverride || columns;
    return (
    <View style={styles.tableHeaderRow} fixed={fixed || undefined}>
      {effectiveCols.map(col => {
        const [line1, line2] = splitHeaderLabel(col.label);
        const sortInfo = sortLookup[col.field];
        const sortFontSize = Math.max(fontSize - 2, 4);
        return (
          <View key={col.field} style={[styles.tableHeaderCell, {
            width: columnWidths[col.field],
            textAlign: col.align || 'left',
            justifyContent: 'flex-start'
          }]}>
            <Text>{line1}</Text>
            <Text style={{ minHeight: sortFontSize + 1 }}>{line2}</Text>
            <Text style={{ fontSize: sortFontSize, color: '#9ca3af', marginTop: 1, minHeight: sortFontSize + 1 }}>
              {sortInfo ? `${sortInfo.priority} ${sortInfo.direction === 'asc' ? 'asc' : 'dsc'}` : ' '}
            </Text>
          </View>
        );
      })}
    </View>
  );
  };

  const DataRows = ({ rows, pageOffset = 0 }) => (
    <View>
      {rows.map((row, idx) => {
        const globalIdx = pageOffset + idx;
        const isZebra = zebraStriping && globalIdx % 2 === 1;
        return (
          <View key={row.id || globalIdx} style={isZebra ? styles.tableRowZebra : styles.tableRow} wrap={false}>
            {columns.map(col => {
              const camelField = toCamelCase(col.field);
              const displayKey = `${camelField}_Display`;
              const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];
              const isWrappable = col.field === 'Note' || col.field === 'Comment' || col.field === 'Note_DES';
              return (
                <View key={col.field} style={[styles.tableCell, {
                  width: columnWidths[col.field],
                  textAlign: isWrappable ? 'left' : (col.align || 'left'),
                  alignItems: isWrappable ? 'flex-start' : undefined,
                  ...(isWrappable ? {} : { height: cellHeight, overflow: 'hidden' })
                }]}>
                  <Text>{displayValue != null ? String(displayValue) : ''}</Text>
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );

  const footerContent = (
    <>
      <Text>{timestamp}</Text>
      {(clientLogoSrc || clientName) && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          {clientLogoSrc && <Image src={clientLogoSrc} style={{ height: 12, maxWidth: 60, objectFit: 'contain' }} />}
          <Text>{clientName ? `${clientName} Confidential` : 'Confidential'}</Text>
        </View>
      )}
      {dawnVersion && <Text>Dawn - {dawnVersion}</Text>}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <Image src={dawnLogo} style={{ height: 14 }} />
        <Text>Wunderlich-Malec Confidential</Text>
      </View>
      <Text render={({ pageNumber, totalPages }) => (pageRange
        ? `Page ${pageRange.start + pageNumber} of ${pageRange.total}`
        : `Page ${pageNumber} of ${totalPages}`)} />
    </>
  );

  // ── Grouped mode: one section per unique group value, subtitle changes per group ──
  const allPages = buildReportPages({ config, data, groupByField, rowsPerPage });
  const pageSlice = allPages && pageRange ? allPages.slice(pageRange.start, pageRange.end) : allPages;
  const firstIdx = pageRange ? pageRange.start : 0;

  if (groupByField && rowsPerPage) {
    const pages = pageSlice;

    return (
      <Document
        title={config.reportName}
        author={config.branding?.companyName || 'Wunderlich Malec Engineering'}
        subject={config.description || ''}
      >
        {pages.map((page, i) => (
          <Page key={firstIdx + i} size={pageSize} orientation="landscape" style={styles.page}
            bookmark={page.offset === 0 ? { title: page.groupVal, fit: '/FitH' } : undefined}
          >
            <PageHeader subtitle={page.subtitle} totalCount={page.groupRowCount} />
            <ColHeaders cols={columnsByGroup?.[page.groupVal] || undefined} />
            <DataRows rows={page.chunk} pageOffset={page.offset} />
            <View style={styles.footer}>{footerContent}</View>
            <WatermarkOverlay />
          </Page>
        ))}
      </Document>
    );
  }

  // ── Manual pagination (no grouping) ──
  if (rowsPerPage) {
    const pages = pageSlice;

    return (
      <Document
        title={config.reportName}
        author={config.branding?.companyName || 'Wunderlich Malec Engineering'}
        subject={config.description || ''}
      >
        {pages.map((page, i) => (
          <Page key={firstIdx + i} size={pageSize} orientation="landscape" style={styles.page}>
            <PageHeader totalCount={data.length} />
            <ColHeaders />
            <DataRows rows={page.chunk} pageOffset={page.offset} />
            <View style={styles.footer}>{footerContent}</View>
            <WatermarkOverlay />
          </Page>
        ))}
      </Document>
    );
  }

  // ── Default — single <Page>, React-PDF auto-paginates ──
  return (
    <Document
      title={config.reportName}
      author={config.branding?.companyName || 'Wunderlich Malec Engineering'}
      subject={config.description || ''}
    >
      <Page size={pageSize} orientation="landscape" style={styles.page}>
        <PageHeader fixed totalCount={data.length} />
        <ColHeaders fixed />
        <DataRows rows={data} />
        <View style={styles.footer} fixed>{footerContent}</View>
        <WatermarkOverlay />
      </Page>
    </Document>
  );
};

export default ReportPdfDocument;
